// Copyright 2025, University of Maryland, All Rights Reserved
import { Fragment, useEffect, useState } from 'react';
import type { ComponentType, SVGProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  X,
  Briefcase,
  ChevronDown,
  ChevronRight,
  DollarSign,
  BarChart3,
  Database,
  FileJson,
  BookOpen,
  Home,
  Users,
  Bot,
  History,
  RotateCcw,
  MessageSquare,
  CalendarClock,
  Plug,
  Building2,
  Workflow,
  LayoutTemplate,
  Sparkles,
  Megaphone,
  Settings,
  AppWindow,
  FileText,
  Plus,
} from 'lucide-react';
import { VaultSafeIcon } from '@/components/VaultSafeIcon';
import { useAuth } from '@/subsystems/workspaces';
import { useUserProfile } from '@/subsystems/workspaces/hooks/use-user-profile';
import { useServices } from '@/app-shell/services';
import { useAgents } from '@/subsystems/agents';
import { useWorkspaceConfig } from '@/app-shell/hooks';
import { useNewSidebarItems } from '@/app-shell/hooks/useNewSidebarItems';
import { useSidebar } from './SidebarContext';
import {
  getWorkspaceVaultPairing,
  getWorkspaceVaultStatus,
} from '@/subsystems/vault/services/vault-api';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  isGlobalAdmin: boolean;
  isWorkspaceAdmin: boolean;
}

export default function Sidebar({ open, onClose, isGlobalAdmin, isWorkspaceAdmin }: SidebarProps) {
  const location = useLocation();
  const sidebar = useSidebar();
  const navigate = useNavigate();
  const { userId, isOrgAdmin, selectedOrganizationId, selectedWorkspaceId, currentWorkspace } = useAuth();
  const { data: userProfile } = useUserProfile();
  const myConnectorGrants = userProfile?.connectorAdminGrants ?? [];
  const hasConnectorGrantInOrg = myConnectorGrants.some(
    grant => grant.orgId === selectedOrganizationId,
  );
  const services = useServices();
  const { data: agents = [] } = useAgents(
    services.getClient(),
    selectedOrganizationId ?? '',
    selectedWorkspaceId ?? '',
  );
  const { dashboardTitle } = useWorkspaceConfig(
    selectedOrganizationId ?? undefined,
    selectedWorkspaceId ?? undefined,
    currentWorkspace?.role !== 'connector-admin',
  );
  const isConnectorAdminHere = currentWorkspace?.role === 'connector-admin';
  const pairing = useQuery({
    queryKey: ['workspace-vault-pairing', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => getWorkspaceVaultPairing(
      services.getClient(),
      selectedOrganizationId!,
      selectedWorkspaceId!,
    ),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId && isConnectorAdminHere),
  });
  const pairingStatus = useQuery({
    queryKey: ['workspace-vault-pairing-status', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => getWorkspaceVaultStatus(
      services.getClient(),
      selectedOrganizationId!,
      selectedWorkspaceId!,
    ),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId && !isConnectorAdminHere),
  });
  // A discovered connector-admin workspace has no readable workspace config
  // or member-safe status. It receives only the pairing identity needed to
  // open Connectors; ordinary members use the status endpoint so pairing
  // administration is never required for Dashboard or Vault navigation.
  const hasConnectorWorkspace = isConnectorAdminHere
    ? Boolean(pairing.data)
    : pairingStatus.data?.enabled === true;
  const [agentsExpanded, setAgentsExpanded] = useState(true);
  const newSidebarItems = useNewSidebarItems(userId, location.pathname);

  useEffect(() => {
    if (location.pathname.startsWith('/agents/')) setAgentsExpanded(true);
  }, [location.pathname]);

  // cm-coding: Users should see Connectors and Vault only when the selected
  // workspace has paired connector storage. Derive both links from that pairing
  // instead of application names so every application gains or loses the two
  // navigation entries together as its workspace configuration changes.
  const sections: Array<{
    heading?: string;
    items: Array<{
      path: string;
      label: string;
      Icon: ComponentType<SVGProps<SVGSVGElement>>;
      testId: string;
      show: boolean;
      isNew?: boolean;
    }>;
  }> = [
    {
      items: [
        // A connector-admin-only workspace has no dashboard or Vault browsing
        // for this user; showing dead entries would imply data access the
        // grant deliberately does not carry.
        { path: '/', label: dashboardTitle || 'Dashboard', Icon: Home, testId: 'sidebar-nav-dashboard', show: !isConnectorAdminHere },
        { path: '/agent-library', label: 'Agent Library', Icon: Sparkles, testId: 'sidebar-nav-agent-library', show: isGlobalAdmin },
        // A connector grant opens the same Connectors page every admin uses;
        // the page's capability map decides which controls unlock.
        { path: '/workspace-connectors', label: 'Connectors', Icon: Plug, testId: 'sidebar-nav-workspace-connectors', show: hasConnectorWorkspace && (isGlobalAdmin || isWorkspaceAdmin || hasConnectorGrantInOrg), isNew: newSidebarItems.has('/workspace-connectors') },
        { path: '/vault', label: 'Vault', Icon: VaultSafeIcon, testId: 'sidebar-nav-vault', show: hasConnectorWorkspace && !isConnectorAdminHere, isNew: newSidebarItems.has('/vault') },
      ],
    },
    {
      heading: 'Administration',
      items: [
        { path: '/users', label: 'User Management', Icon: Users, testId: 'sidebar-nav-users', show: isGlobalAdmin || isOrgAdmin || isWorkspaceAdmin },
        { path: '/workspace-settings', label: 'Settings', Icon: Settings, testId: 'sidebar-nav-workspace-settings', show: isGlobalAdmin || isWorkspaceAdmin },
      ],
    },
    {
      heading: 'Content',
      items: [
        { path: '/schemas', label: 'Schemas', Icon: FileJson, testId: 'sidebar-nav-schemas', show: isGlobalAdmin },
        { path: '/knowledge-bases', label: 'Knowledge Bases', Icon: BookOpen, testId: 'sidebar-nav-knowledge-bases', show: isGlobalAdmin },
        { path: '/datasets', label: 'Datasets', Icon: Database, testId: 'sidebar-nav-datasets', show: isGlobalAdmin },
      ],
    },
    {
      heading: 'Configuration',
      items: [
        { path: '/layout', label: 'Layout', Icon: LayoutTemplate, testId: 'sidebar-nav-layout', show: isGlobalAdmin },
        { path: '/pipelines', label: 'Pipelines', Icon: Workflow, testId: 'sidebar-nav-pipelines', show: isGlobalAdmin },
      ],
    },
    {
      heading: 'Operations',
      items: [
        { path: '/jobs', label: 'Jobs', Icon: Briefcase, testId: 'sidebar-nav-jobs', show: isGlobalAdmin },
        { path: '/costs', label: 'Costs', Icon: DollarSign, testId: 'sidebar-nav-costs', show: isGlobalAdmin || isWorkspaceAdmin },
        { path: '/metrics', label: 'Metrics', Icon: BarChart3, testId: 'sidebar-nav-metrics', show: isGlobalAdmin },
      ],
    },
    {
      heading: 'System',
      items: [
        { path: '/orgs', label: 'Org & Workspace Manager', Icon: Building2, testId: 'sidebar-nav-orgs', show: isGlobalAdmin },
        { path: '/applications', label: 'Applications', Icon: AppWindow, testId: 'sidebar-nav-applications', show: isGlobalAdmin },
        { path: '/connectors', label: 'System Connectors', Icon: Plug, testId: 'sidebar-nav-connectors', show: isGlobalAdmin },
        { path: '/system-prompts', label: 'System Prompts', Icon: MessageSquare, testId: 'sidebar-nav-system-prompts', show: isGlobalAdmin },
        { path: '/llm-config', label: 'LLM Config', Icon: Bot, testId: 'sidebar-nav-llm-config', show: isGlobalAdmin },
        { path: '/scheduled-jobs', label: 'Scheduled Jobs', Icon: CalendarClock, testId: 'sidebar-nav-scheduled-jobs', show: isGlobalAdmin },
        { path: '/migrations', label: 'Migrations', Icon: History, testId: 'sidebar-nav-migrations', show: isGlobalAdmin },
        { path: '/data-restore', label: 'Data Restore', Icon: RotateCcw, testId: 'sidebar-nav-data-restore', show: isGlobalAdmin },
        { path: '/site-banner', label: 'Site Banner', Icon: Megaphone, testId: 'sidebar-nav-site-banner', show: isGlobalAdmin },
      ],
    },
  ];

  const visibleSections = sections
    .map(section => ({ ...section, items: section.items.filter(item => item.show) }))
    .filter(section => section.items.length > 0);

  return (
    <aside
      ref={(element) => {
        if (open) element?.removeAttribute('inert');
        else element?.setAttribute('inert', '');
      }}
      className={`relative z-[2] flex-none overflow-hidden bg-white transition-[width,box-shadow] duration-[260ms] ease-[cubic-bezier(.32,.72,.32,1)] ${open ? 'w-64 shadow-[inset_-1px_0_0_#e5e7eb,2px_0_8px_rgba(15,23,42,.03)]' : 'w-0 shadow-none'}`}
      aria-label="Sidebar"
      aria-hidden={!open}
    >
      <div className={`flex h-full w-64 flex-col transition-[opacity,transform] ${open ? 'translate-x-0 opacity-100 duration-[260ms] ease-[cubic-bezier(.32,.72,.32,1)]' : '-translate-x-3.5 opacity-0 duration-200 ease-in'}`}>
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-[7px] bg-slate-900 text-white">
              <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <span className="text-[14.5px] font-semibold tracking-[-0.01em] text-gray-900">DocReview</span>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center p-1 rounded-md text-gray-700 hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <nav className="p-2 flex-1 overflow-y-auto">
          {visibleSections.map((section, idx) => (
            <Fragment key={section.heading ?? `section-${idx}`}>
              <div className={idx > 0 ? 'mt-4' : ''}>
                {section.heading && (
                  <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {section.heading}
                  </div>
                )}
                {section.items.map(({ path, label, Icon, testId, isNew }) => {
                  const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
                  return (
                    <button
                      key={path}
                      type="button"
                      data-testid={testId}
                      onClick={() => navigate(path)}
                      aria-current={isActive ? 'page' : undefined}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md transition-colors duration-150 ${
                        isActive
                          ? 'bg-gray-200 text-gray-900 font-medium shadow-[inset_3px_0_0_#475569]'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span>{label}</span>
                      {isNew && (
                        <span className="ml-auto rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold tracking-wide text-indigo-700">
                          NEW
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {idx === 0 && (isGlobalAdmin || isWorkspaceAdmin) && (
                <div className="mt-4">
                  <button
                    type="button"
                    data-testid="sidebar-agents-toggle"
                    onClick={() => setAgentsExpanded(v => !v)}
                    aria-expanded={agentsExpanded}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-100 rounded-md"
                  >
                    {agentsExpanded ? (
                      <ChevronDown className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    )}
                    <Bot className="h-4 w-4" aria-hidden="true" />
                    <span>Agents ({agents.length})</span>
                  </button>
                  {agentsExpanded && agents.map(agent => {
                    const path = `/agents/${agent.agentId}`;
                    const isActive = location.pathname === path;
                    return (
                      <button
                        key={agent.agentId}
                        type="button"
                        data-testid={`sidebar-nav-agent-${agent.agentId}`}
                        onClick={() => navigate(path)}
                        aria-current={isActive ? 'page' : undefined}
                        className={`w-full flex items-center gap-2 pl-9 pr-3 py-2 text-sm rounded-md transition-colors duration-150 ${
                          isActive
                            ? 'bg-gray-200 text-gray-900 font-medium shadow-[inset_3px_0_0_#475569]'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className="truncate">{agent.label}</span>
                        {agent.agentId === sidebar?.newAgentId && (
                          <span className="docreview-pop ml-auto rounded-full border border-blue-100 bg-blue-50 px-[7px] py-px text-[10px] font-bold tracking-[.05em] text-blue-600">NEW</span>
                        )}
                      </button>
                    );
                  })}
                  {agentsExpanded && isGlobalAdmin && (
                    <button
                      type="button"
                      data-testid="sidebar-nav-create-agent"
                      onClick={() => navigate('/agents/new')}
                      aria-current={location.pathname === '/agents/new' ? 'page' : undefined}
                      className={`w-full flex items-center gap-2 pl-9 pr-3 py-2 text-sm rounded-md transition-colors duration-150 ${location.pathname === '/agents/new'
                        ? 'bg-gray-200 text-gray-900 font-medium shadow-[inset_3px_0_0_#475569]'
                        : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      <span>Create New Agent</span>
                    </button>
                  )}
                </div>
              )}
            </Fragment>
          ))}
        </nav>
      </div>
    </aside>
  );
}
